import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Icon from "@mui/material/Icon";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { adminRequest } from "services/adminApi";

const choices = [
  {
    value: "classic",
    title: "التصميم الحالي",
    description: "بطاقات خدمات يا مسافر الأفقية الموجودة حالياً.",
  },
  {
    value: "visualGrid",
    title: "خدماتنا المصوّرة",
    description: "شبكة RTL من ست بطاقات مصوّرة بثلاثة أعمدة.",
  },
];

const defaultUpdateSettings = {
  mode: "none",
  minimumVersion: "",
  titleAr: "يتوفر تحديث جديد",
  messageAr: "يتوفر إصدار جديد من يا مسافر. حدّث التطبيق الآن للمتابعة.",
};

const updateModeLabels = {
  none: "بدون تحديث",
  optional: "تحديث اختياري",
  required: "تحديث إجباري",
};

const versionParts = (value) =>
  value
    .trim()
    .split(".")
    .map((part) => Number.parseInt(part, 10));

const isVersionAtMost = (value, available) => {
  if (!/^\d+\.\d+\.\d+(?:\.\d+)?$/.test(value) || !available) return false;
  const left = versionParts(value);
  const right = versionParts(available);
  const length = Math.max(left.length, right.length);
  for (let index = 0; index < length; index += 1) {
    const difference = (left[index] || 0) - (right[index] || 0);
    if (difference !== 0) return difference < 0;
  }
  return true;
};

function UpdatePlatformCard({ platform, label, value, store, metrics, onChange }) {
  const enabled = value.mode !== "none";
  const storeReady = store?.available === true;
  const versionAllowed = !enabled || isVersionAtMost(value.minimumVersion, store?.version || "");
  const update = (field, nextValue) => onChange({ ...value, [field]: nextValue });

  return (
    <MDBox p={2.5} borderRadius="xl" sx={{ border: "1px solid #dde5e7" }}>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MDTypography variant="h6" fontWeight="bold">
          {label}
        </MDTypography>
        <MDTypography variant="caption" color={storeReady ? "success" : "error"} fontWeight="bold">
          {storeReady ? `المتجر: ${store.version}` : "لم يتم التحقق من المتجر"}
        </MDTypography>
      </MDBox>
      <TextField
        select
        fullWidth
        label="حالة التحديث"
        value={value.mode}
        onChange={(event) => update("mode", event.target.value)}
      >
        {Object.entries(updateModeLabels).map(([mode, modeLabel]) => (
          <MenuItem key={mode} value={mode}>
            {modeLabel}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        fullWidth
        sx={{ mt: 2 }}
        label="أقل إصدار مستهدف"
        placeholder="1.0.3"
        value={value.minimumVersion}
        onChange={(event) => update("minimumVersion", event.target.value)}
        inputProps={{ dir: "ltr" }}
      />
      <TextField
        fullWidth
        sx={{ mt: 2 }}
        label="عنوان الرسالة"
        value={value.titleAr}
        onChange={(event) => update("titleAr", event.target.value)}
        inputProps={{ maxLength: 80 }}
      />
      <TextField
        fullWidth
        multiline
        minRows={2}
        sx={{ mt: 2 }}
        label="رسالة التحديث"
        value={value.messageAr}
        onChange={(event) => update("messageAr", event.target.value)}
        inputProps={{ maxLength: 240 }}
      />
      {enabled && !versionAllowed && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          يجب أن يكون الإصدار صالحًا وألا يتجاوز الإصدار المتوفر في المتجر.
        </Alert>
      )}
      <MDBox mt={2} p={2} borderRadius="lg" sx={{ background: "#f2faf8" }}>
        <MDTypography variant="caption" color="text" display="block">
          معاينة {updateModeLabels[value.mode]}
        </MDTypography>
        <MDTypography variant="h6" fontWeight="bold" mt={0.5}>
          {value.titleAr || "عنوان التحديث"}
        </MDTypography>
        <MDTypography variant="button" color="text" display="block" mt={0.5}>
          {value.messageAr || "رسالة التحديث"}
        </MDTypography>
        {enabled && (
          <MDBox display="flex" gap={1} mt={1.5}>
            <Button size="small" variant="contained" color="success">
              تحديث الآن
            </Button>
            {value.mode === "optional" && <Button size="small">لاحقًا</Button>}
          </MDBox>
        )}
      </MDBox>
      <MDBox display="grid" gap={1} mt={2} sx={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
        {[
          ["الظهور", metrics?.shown || 0],
          ["تحديث", metrics?.updateClicked || 0],
          ["لاحقًا", metrics?.laterClicked || 0],
          ["النسبة", `${metrics?.updateRate || 0}%`],
        ].map(([title, metric]) => (
          <MDBox key={title} textAlign="center" p={1} borderRadius="md" bgColor="grey-100">
            <MDTypography variant="caption" color="text" display="block">
              {title}
            </MDTypography>
            <MDTypography variant="button" fontWeight="bold">
              {metric}
            </MDTypography>
          </MDBox>
        ))}
      </MDBox>
      <MDTypography variant="caption" color="text" display="block" mt={1}>
        {platform === "ios" ? "App Store / iPhone وiPad" : "Google Play / Android"}
      </MDTypography>
    </MDBox>
  );
}

UpdatePlatformCard.propTypes = {
  platform: PropTypes.oneOf(["ios", "android"]).isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.shape({
    mode: PropTypes.string.isRequired,
    minimumVersion: PropTypes.string.isRequired,
    titleAr: PropTypes.string.isRequired,
    messageAr: PropTypes.string.isRequired,
  }).isRequired,
  store: PropTypes.shape({ available: PropTypes.bool, version: PropTypes.string }),
  metrics: PropTypes.shape({
    shown: PropTypes.number,
    updateClicked: PropTypes.number,
    laterClicked: PropTypes.number,
    updateRate: PropTypes.number,
  }),
  onChange: PropTypes.func.isRequired,
};

UpdatePlatformCard.defaultProps = { store: null, metrics: null };

function LayoutPreview({ value }) {
  if (value === "classic") {
    return (
      <MDBox display="flex" gap={1} sx={{ direction: "rtl", overflow: "hidden" }}>
        {["المطار", "التحضيرات", "رحلاتي"].map((label) => (
          <MDBox key={label} bgColor="grey-100" borderRadius="lg" px={2} py={2} minWidth="92px">
            <Icon color="success">travel_explore</Icon>
            <MDTypography variant="caption" fontWeight="bold" display="block">
              {label}
            </MDTypography>
          </MDBox>
        ))}
      </MDBox>
    );
  }
  return (
    <MDBox
      display="grid"
      gap={1}
      sx={{ direction: "rtl", gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}
    >
      {["المطار", "رحلاتي", "التحضيرات", "الإرشادات", "ميزانيتي", "اكتشف المزيد"].map(
        (label, index) => (
          <MDBox
            key={label}
            borderRadius="lg"
            p={1.5}
            minHeight="94px"
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            sx={{
              color: "white",
              background: `linear-gradient(145deg, ${index % 2 ? "#154f58" : "#0b3540"}, #071f27)`,
            }}
          >
            <MDTypography variant="button" color="white" fontWeight="bold">
              {label}
            </MDTypography>
            <Icon>flight_takeoff</Icon>
          </MDBox>
        )
      )}
    </MDBox>
  );
}

LayoutPreview.propTypes = {
  value: PropTypes.oneOf(["classic", "visualGrid"]).isRequired,
};

function AppSettingsPage() {
  const [selected, setSelected] = useState("classic");
  const [active, setActive] = useState("classic");
  const [tripItineraryEnabled, setTripItineraryEnabled] = useState(true);
  const [activeTripItineraryEnabled, setActiveTripItineraryEnabled] = useState(true);
  const [exploreRestaurantsEnabled, setExploreRestaurantsEnabled] = useState(true);
  const [activeExploreRestaurantsEnabled, setActiveExploreRestaurantsEnabled] = useState(true);
  const [feedbackPromptSnoozeDays, setFeedbackPromptSnoozeDays] = useState(3);
  const [activeFeedbackPromptSnoozeDays, setActiveFeedbackPromptSnoozeDays] = useState(3);
  const [updates, setUpdates] = useState({
    ios: { ...defaultUpdateSettings },
    android: { ...defaultUpdateSettings },
  });
  const [activeUpdates, setActiveUpdates] = useState({
    ios: { ...defaultUpdateSettings },
    android: { ...defaultUpdateSettings },
  });
  const [storeVersions, setStoreVersions] = useState({ ios: null, android: null });
  const [updateMetrics, setUpdateMetrics] = useState({ ios: {}, android: {} });
  const [checkingStores, setCheckingStores] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    adminRequest("/admin/app-settings/home")
      .then((payload) => {
        const layout = payload?.homeServicesLayout || "classic";
        setSelected(layout);
        setActive(layout);
        const itineraryEnabled = payload?.tripItineraryEnabled !== false;
        const restaurantsEnabled = payload?.exploreRestaurantsEnabled !== false;
        setTripItineraryEnabled(itineraryEnabled);
        setActiveTripItineraryEnabled(itineraryEnabled);
        setExploreRestaurantsEnabled(restaurantsEnabled);
        setActiveExploreRestaurantsEnabled(restaurantsEnabled);
        const snoozeDays = Number(payload?.feedbackPromptSnoozeDays || 3);
        setFeedbackPromptSnoozeDays(snoozeDays);
        setActiveFeedbackPromptSnoozeDays(snoozeDays);
        const nextUpdates = {
          ios: {
            mode: payload?.iosUpdateMode || "none",
            minimumVersion: payload?.minimumIosVersion || "",
            titleAr: payload?.iosUpdateTitleAr || defaultUpdateSettings.titleAr,
            messageAr: payload?.iosUpdateMessageAr || defaultUpdateSettings.messageAr,
          },
          android: {
            mode: payload?.androidUpdateMode || "none",
            minimumVersion: payload?.minimumAndroidVersion || "",
            titleAr: payload?.androidUpdateTitleAr || defaultUpdateSettings.titleAr,
            messageAr: payload?.androidUpdateMessageAr || defaultUpdateSettings.messageAr,
          },
        };
        setUpdates(nextUpdates);
        setActiveUpdates(nextUpdates);
        setUpdateMetrics(payload?.updateMetrics || { ios: {}, android: {} });
      })
      .catch((requestError) => setError(requestError.message))
      .finally(() => setLoading(false));
  }, []);

  const checkStores = async () => {
    setCheckingStores(true);
    setError("");
    try {
      const payload = await adminRequest("/admin/app-settings/store-versions");
      setStoreVersions({ ios: payload?.ios || null, android: payload?.android || null });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setCheckingStores(false);
    }
  };

  useEffect(() => {
    checkStores();
  }, []);

  const updatesDirty = JSON.stringify(updates) !== JSON.stringify(activeUpdates);
  const platformValid = (platform) => {
    const value = updates[platform];
    if (value.mode === "none") return true;
    return (
      storeVersions[platform]?.available === true &&
      isVersionAtMost(value.minimumVersion, storeVersions[platform]?.version || "") &&
      value.titleAr.trim().length > 0 &&
      value.messageAr.trim().length > 0
    );
  };
  const updateSettingsValid = platformValid("ios") && platformValid("android");
  const feedbackSettingsValid =
    Number.isInteger(feedbackPromptSnoozeDays) &&
    feedbackPromptSnoozeDays >= 1 &&
    feedbackPromptSnoozeDays <= 90;

  const save = async () => {
    if (
      saving ||
      (selected === active &&
        tripItineraryEnabled === activeTripItineraryEnabled &&
        exploreRestaurantsEnabled === activeExploreRestaurantsEnabled &&
        feedbackPromptSnoozeDays === activeFeedbackPromptSnoozeDays &&
        !updatesDirty)
    )
      return;
    setConfirmOpen(false);
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const payload = await adminRequest("/admin/app-settings/home", {
        method: "PATCH",
        body: JSON.stringify({
          homeServicesLayout: selected,
          tripItineraryEnabled,
          exploreRestaurantsEnabled,
          feedbackPromptSnoozeDays,
          iosUpdateMode: updates.ios.mode,
          androidUpdateMode: updates.android.mode,
          minimumIosVersion: updates.ios.minimumVersion.trim(),
          minimumAndroidVersion: updates.android.minimumVersion.trim(),
          iosUpdateTitleAr: updates.ios.titleAr.trim(),
          androidUpdateTitleAr: updates.android.titleAr.trim(),
          iosUpdateMessageAr: updates.ios.messageAr.trim(),
          androidUpdateMessageAr: updates.android.messageAr.trim(),
        }),
      });
      setActive(payload.homeServicesLayout);
      setTripItineraryEnabled(payload.tripItineraryEnabled !== false);
      setActiveTripItineraryEnabled(payload.tripItineraryEnabled !== false);
      setExploreRestaurantsEnabled(payload.exploreRestaurantsEnabled !== false);
      setActiveExploreRestaurantsEnabled(payload.exploreRestaurantsEnabled !== false);
      setFeedbackPromptSnoozeDays(payload.feedbackPromptSnoozeDays || 3);
      setActiveFeedbackPromptSnoozeDays(payload.feedbackPromptSnoozeDays || 3);
      const savedUpdates = {
        ios: {
          mode: payload.iosUpdateMode || "none",
          minimumVersion: payload.minimumIosVersion || "",
          titleAr: payload.iosUpdateTitleAr || defaultUpdateSettings.titleAr,
          messageAr: payload.iosUpdateMessageAr || defaultUpdateSettings.messageAr,
        },
        android: {
          mode: payload.androidUpdateMode || "none",
          minimumVersion: payload.minimumAndroidVersion || "",
          titleAr: payload.androidUpdateTitleAr || defaultUpdateSettings.titleAr,
          messageAr: payload.androidUpdateMessageAr || defaultUpdateSettings.messageAr,
        },
      };
      setUpdates(savedUpdates);
      setActiveUpdates(savedUpdates);
      setMessage("تم حفظ الإعدادات وتفعيلها في التطبيق.");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} dir="rtl">
        <MDTypography variant="h4" fontWeight="bold">
          إعدادات الصفحة الرئيسية
        </MDTypography>
        <MDTypography variant="button" color="text">
          اختر شكل قسم الخدمات الذي يظهر للمسافرين. التصميم القديم سيبقى محفوظاً ويمكن الرجوع إليه.
        </MDTypography>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {message && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {message}
          </Alert>
        )}
        <MDBox bgColor="white" borderRadius="xl" shadow="sm" p={3} mt={3}>
          {loading ? (
            <MDBox textAlign="center" py={5}>
              <CircularProgress color="success" />
            </MDBox>
          ) : (
            <>
              <MDBox
                display="grid"
                gap={2}
                sx={{ gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" } }}
              >
                {choices.map((choice) => {
                  const isSelected = selected === choice.value;
                  return (
                    <MDBox
                      key={choice.value}
                      component="button"
                      type="button"
                      onClick={() => setSelected(choice.value)}
                      p={2}
                      borderRadius="xl"
                      textAlign="right"
                      sx={{
                        cursor: "pointer",
                        border: "2px solid",
                        borderColor: isSelected ? "#0b8f78" : "#e4e8eb",
                        background: isSelected ? "#f1fbf8" : "white",
                      }}
                    >
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                      >
                        <MDTypography variant="h6" fontWeight="bold">
                          {choice.title}
                        </MDTypography>
                        {active === choice.value && (
                          <MDTypography variant="caption" color="success" fontWeight="bold">
                            مفعّل الآن
                          </MDTypography>
                        )}
                      </MDBox>
                      <MDTypography variant="caption" color="text" display="block" mb={2}>
                        {choice.description}
                      </MDTypography>
                      <LayoutPreview value={choice.value} />
                    </MDBox>
                  );
                })}
              </MDBox>
              <MDBox mt={3} p={2} borderRadius="lg" bgColor="grey-100">
                <MDTypography variant="h6" fontWeight="bold" mb={1}>
                  اقتراحاتكم والتقييم
                </MDTypography>
                <MDTypography variant="caption" color="text" display="block" mb={2}>
                  عدد الأيام قبل إعادة عرض النافذة عندما يختار المستخدم «ذكرني لاحقًا».
                </MDTypography>
                <TextField
                  label="إعادة التذكير بعد (أيام)"
                  type="number"
                  value={feedbackPromptSnoozeDays}
                  onChange={(event) => setFeedbackPromptSnoozeDays(Number(event.target.value))}
                  error={!feedbackSettingsValid}
                  inputProps={{ min: 1, max: 90 }}
                  helperText="القيمة المسموحة من يوم واحد إلى 90 يومًا. الافتراضي 3 أيام."
                  sx={{ width: { xs: "100%", sm: 320 } }}
                />
              </MDBox>
              <MDBox mt={3} p={2} borderRadius="lg" bgColor="grey-100">
                <MDTypography variant="h6" fontWeight="bold" mb={1}>
                  أقسام التطبيق
                </MDTypography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={tripItineraryEnabled}
                      onChange={(event) => setTripItineraryEnabled(event.target.checked)}
                    />
                  }
                  label="إظهار برنامج الرحلة في سفرتي"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={exploreRestaurantsEnabled}
                      onChange={(event) => setExploreRestaurantsEnabled(event.target.checked)}
                    />
                  }
                  label="إظهار قسم المطاعم في استكشف"
                />
              </MDBox>
              <MDBox mt={3} p={2} borderRadius="lg" bgColor="grey-100">
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <MDBox>
                    <MDTypography variant="h6" fontWeight="bold">
                      إدارة إصدار التطبيق
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      إعداد مستقل لكل نظام، مع تحديث اختياري مرة واحدة أو تحديث إجباري.
                    </MDTypography>
                  </MDBox>
                  <Button variant="outlined" color="success" onClick={checkStores}>
                    {checkingStores ? "جارٍ الفحص..." : "فحص المتاجر"}
                  </Button>
                </MDBox>
                <MDBox
                  display="grid"
                  gap={2}
                  sx={{ gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}
                >
                  <UpdatePlatformCard
                    platform="ios"
                    label="iOS"
                    value={updates.ios}
                    store={storeVersions.ios}
                    metrics={
                      updateMetrics.ios?.versions?.[updates.ios.minimumVersion] || updateMetrics.ios
                    }
                    onChange={(value) => setUpdates((current) => ({ ...current, ios: value }))}
                  />
                  <UpdatePlatformCard
                    platform="android"
                    label="Android"
                    value={updates.android}
                    store={storeVersions.android}
                    metrics={
                      updateMetrics.android?.versions?.[updates.android.minimumVersion] ||
                      updateMetrics.android
                    }
                    onChange={(value) => setUpdates((current) => ({ ...current, android: value }))}
                  />
                </MDBox>
                {!updateSettingsValid && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    لا يمكن التفعيل قبل نجاح فحص المتجر والتأكد أن الإصدار المستهدف متاح.
                  </Alert>
                )}
              </MDBox>
              <MDBox mt={3} display="flex" justifyContent="flex-start">
                <Button
                  variant="contained"
                  color="success"
                  disabled={
                    saving ||
                    (selected === active &&
                      tripItineraryEnabled === activeTripItineraryEnabled &&
                      exploreRestaurantsEnabled === activeExploreRestaurantsEnabled &&
                      feedbackPromptSnoozeDays === activeFeedbackPromptSnoozeDays &&
                      !updatesDirty) ||
                    !updateSettingsValid ||
                    !feedbackSettingsValid
                  }
                  onClick={() => setConfirmOpen(true)}
                >
                  {saving ? "جارٍ الحفظ..." : "حفظ وتفعيل"}
                </Button>
              </MDBox>
              <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} dir="rtl">
                <DialogTitle>تأكيد تفعيل إعدادات التطبيق</DialogTitle>
                <DialogContent>
                  سيتم تطبيق التغييرات المحفوظة على التطبيق فورًا. هل تريد المتابعة؟
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setConfirmOpen(false)}>إلغاء</Button>
                  <Button variant="contained" color="success" onClick={save}>
                    تأكيد وتفعيل
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
}

export default AppSettingsPage;
